
import React, { useEffect, useState } from 'react';

const StageSnapshot = ({ stages }) => {
    const [animatedStages, setAnimatedStages] = useState([]);

    useEffect(() => {
        if (!stages || stages.length === 0) {
            setAnimatedStages([]);
            return;
        }

        const duration = 1000;
        const frameRate = 60;
        const totalFrames = Math.round(duration * frameRate / 1000);
        let frame = 0;

        const counter = setInterval(() => {
            frame++;
            const progress = frame / totalFrames;
            const easeOut = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

            if (frame <= totalFrames) {
                const currentStages = stages.map(stage => ({
                    ...stage,
                    currentPercentage: Math.round((Number(stage.percentage) || 0) * easeOut)
                }));
                setAnimatedStages(currentStages);
            } else {
                const finalStages = stages.map(stage => ({
                    ...stage,
                    currentPercentage: Math.round(Number(stage.percentage) || 0)
                }));
                setAnimatedStages(finalStages);
                clearInterval(counter);
            }
        }, 1000 / frameRate);

        return () => clearInterval(counter);
    }, [stages]);

    // If stages exist but animation hasn't started/finished first frame (rare but possible), 
    // we want to ensure we don't show empty or flash. 
    // However, animatedStages being empty initially is fine as it falls back to empty render or we can init with 0s.
    // Better to use `stages` for structure and `animatedStages[index]` for values if available.

    // Combining the source of truth to avoid mismatch during init:
    const displayStages = animatedStages.length === stages?.length ? animatedStages : stages?.map(s => ({ ...s, currentPercentage: 0 })) || [];

    return (
        <div className="bg-white rounded-2xl p-6 shadow-md h-full">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Stage Snapshot</h3>
            {(!stages || stages.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-full py-8 px-4">
                    {/* Icon */}
                    <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                        <svg
                            className="w-8 h-8 text-blue-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                        </svg>
                    </div>

                    {/* Main Message */}
                    <h4 className="text-gray-900 font-semibold text-base mb-2">
                        No Stage Data
                    </h4>

                    {/* Descriptive Text */}
                    <p className="text-gray-500 text-sm text-center max-w-xs">
                        Stage progress will be displayed here once activities are tracked for today.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-5">
                    {displayStages.map((stage, index) => (
                        <div key={index} className="flex flex-col gap-1">
                            <div className="flex justify-between items-center text-sm font-medium text-gray-700">
                                <span>{stage.name}</span>
                                <span>{stage.currentPercentage}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                    className="h-2 rounded-full"
                                    style={{
                                        width: `${stage.currentPercentage}%`,
                                        backgroundColor: stage.color,
                                        transition: 'width 0.1s linear'
                                    }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StageSnapshot;
